// MonthlyOperation.jsx
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./MonthlyOperation.css";
import { format } from "date-fns";
import { fetchMonthlyAPI } from "../../../../utils/api/operations";
import { Context } from "../../../../context/Context";
import { translations } from "../../../../utils/lang";
import {
  createGrantTotalPercent,
  createServiceChargePercent,
} from "../../../../utils/helpers";
import {
  Circle,
  CircleArrowDown,
  CircleArrowUp,
  Input,
  SaudiRiyal,
  Sigma,
  StarHalf,
} from "../../../ui";
import FilterGroup from "../../../analysis/filter/FilterUI";
import { parseMonthInput } from "../../operationUtils";
import { normalizeDeliveryAppKey, normalizeIntegrationKey } from "../../../../utils/integrations";

const aggregateMonthlyData = (list = []) => {
  return list.reduce(
    (acc, curr) => {
      acc.app = curr.app || "";
      acc.completed += curr.completed || 0;
      acc.completedAmount += curr.completedAmount || 0;
      acc.serviceCharge += curr.serviceCharge || 0;
      acc.offers += curr.offers || 0;
      acc.deliveryOffer += curr.deliveryOffer || 0;
      acc.grandTotal += curr.grandTotal || 0;
      acc.paidCampains += curr.paidCampains || 0;
      return acc;
    },
    {
      app: "",
      completed: 0,
      completedAmount: 0,
      serviceCharge: 0,
      offers: 0,
      deliveryOffer: 0,
      grandTotal: 0,
      paidCampains: 0,
    },
  );
};

export default function MonthlyOperation({
  storeId,
  appId,
  monthlyDate,
  appName,
}) {
  const { handleTokenExpiration, lang } = useContext(Context);
  const tokenExpiredRef = useRef(handleTokenExpiration);
  tokenExpiredRef.current = handleTokenExpiration;
  // 1-based month, as the backend expects. The parent always passes
  // monthlyDate; the fallback mirrors its default (previous month).
  const [month, setMonth] = useState(
    () => monthlyDate?.month || new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).getMonth() + 1,
  );
  const [year, setYear] = useState(
    () => monthlyDate?.year || new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1).getFullYear(),
  );
  const [loadFailed, setLoadFailed] = useState(false);
  const [data, setData] = useState([]);
  const [prevData, setPrevData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});

  const branches = useMemo(
    () => [...new Set(data.map((d) => d.branchName))],
    [data],
  );

  const handleFilterChange = (category, values) => {
    setFilters((prev) => ({
      ...prev,
      [category]: values,
    }));
  };

  useEffect(() => {
    if (!monthlyDate?.month || !monthlyDate?.year) return;

    setMonth(monthlyDate.month);
    setYear(monthlyDate.year);
  }, [monthlyDate]);

  const filteredData = useMemo(() => {
    if (!filters.branch || filters.branch.length === 0) return data;
    return data.filter((d) => filters.branch.includes(d.branchName));
  }, [data, filters]);

  const filteredPrevData = useMemo(() => {
    if (!filters.branch || filters.branch.length === 0) return prevData;
    return prevData.filter((d) => filters.branch.includes(d.branchName));
  }, [prevData, filters]);

  const aggregatedData = useMemo(
    () => aggregateMonthlyData(filteredData),
    [filteredData],
  );

  const aggregatedPrevData = useMemo(
    () => aggregateMonthlyData(filteredPrevData),
    [filteredPrevData],
  );

  useEffect(() => {
    const token = localStorage.getItem("auth-token");
    if (!token) {
      tokenExpiredRef.current();
      return undefined;
    }

    // Ignore a response for a month that is no longer selected.
    let active = true;
    setLoading(true);
    setLoadFailed(false);
    fetchMonthlyAPI(token, {
      month,
      year,
      storeId,
      appId,
      appName: normalizeIntegrationKey(appName) || appName,
      isLud: normalizeIntegrationKey(appName) === "LUD",
    }).then((storesData) => {
      if (!active) return;
      // The helper resolves to undefined when the request failed.
      if (storesData) {
        setData(storesData.current || []);
        setPrevData(storesData.previous || []);
      } else {
        // Don't leave the previous month's figures under the new month.
        setData([]);
        setPrevData([]);
        setLoadFailed(true);
      }
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [storeId, appId, appName, month, year]);

  const subscription = createServiceChargePercent({
    completedAmount: aggregatedData?.completedAmount,
    serviceCharge: aggregatedData?.serviceCharge,
    offers: aggregatedData?.offers,
    deliveryOffer:
      normalizeDeliveryAppKey(aggregatedData?.app) === "keeta" ? aggregatedData?.deliveryOffer : 0,
  });

  const preSubscription = createServiceChargePercent({
    completedAmount: aggregatedPrevData?.completedAmount,
    serviceCharge: aggregatedPrevData?.serviceCharge,
    offers: aggregatedPrevData?.offers,
    deliveryOffer:
      normalizeDeliveryAppKey(aggregatedPrevData?.app) === "keeta"
        ? aggregatedPrevData?.deliveryOffer
        : 0,
  });

  const deduction = createGrantTotalPercent({
    completedAmount: aggregatedData?.completedAmount,
    grandTotal: aggregatedData?.grandTotal - aggregatedData?.paidCampains,
    offers: aggregatedData?.offers,
  });

  const preDeduction = createGrantTotalPercent({
    completedAmount: aggregatedPrevData?.completedAmount,
    grandTotal:
      aggregatedPrevData?.grandTotal - aggregatedPrevData?.paidCampains,
    offers: aggregatedPrevData?.offers,
  });

  const totalOrders = aggregatedData?.completed || 0;
  const totalAmount = aggregatedData?.completedAmount.toFixed(2) || 0;
  const avgAmount = totalOrders ? (totalAmount / totalOrders).toFixed(2) : 0;
  const preTotalOrders = aggregatedPrevData?.completed || 0;
  const preTotalAmount = aggregatedPrevData?.completedAmount.toFixed(2) || 0;
  const preAvgAmount = preTotalOrders
    ? (preTotalAmount / preTotalOrders).toFixed(2)
    : 0;

  const coloring = (current, pre) => {
    // Equal values (including 0 vs 0, which divides to NaN) are neutral.
    if (Number(current) === Number(pre)) return "square";
    const value = (current - pre) / pre;
    if (value > 0) {
      return "up";
    } else if (value === 0) {
      return "square";
    } else {
      return "down";
    }
  };

  const Arrowing = (current, pre) => {
    const value = (current - pre) / pre;
    if (value > 0) {
      return <CircleArrowUp className="arrowing-icon" />;
    } else if (value < 0) {
      return <CircleArrowDown className="arrowing-icon" />;
    } else {
      return <Circle className="arrowing-icon" />;
    }
  };

  return (
    <div className="monthly-operation-card" aria-busy={loading || undefined}>
      <div className="monthly-operation-header">
        <Input
          type="month"
          value={format(new Date(year, month - 1), "yyyy-MM")}
          onChange={(e) => {
            // A cleared input would otherwise become NaN and crash `format`.
            const parsed = parseMonthInput(e.target.value);
            if (!parsed) return;
            setYear(parsed.year);
            setMonth(parsed.month);
          }}
          aria-label={lang === "ar" ? "الشهر" : "Month"}
          fullWidth={false}
        />

        <FilterGroup
          label="Branch"
          options={branches}
          selected={filters?.branch || []}
          onChange={(vals) => handleFilterChange("branch", vals)}
        />
      </div>

      {loadFailed && (
        <p className="monthly-operation-error" role="status">
          {lang === "ar"
            ? "تعذر تحميل بيانات هذا الشهر"
            : "This month's data could not be loaded"}
        </p>
      )}

      <div className="monthly-operation-top">
        <div className="monthly-operation-metric">
          <div className="monthly-operation-label">
            {translations[lang].Operation.subscription}
          </div>
          <span
            className={`monthly-opr-small-value ${coloring(subscription, preSubscription)}`}
          >
            {preSubscription
              ? (
                  ((subscription - preSubscription) / preSubscription) *
                  100
                ).toFixed(2)
              : 0}
            %
          </span>
          <div className="monthly-operation-value-container">
            <span className="monthly-operation-value">{subscription}%</span>
            <span
              className={`monthly-operation-value ${coloring(subscription, preSubscription)}`}
            >
              {Arrowing(subscription, preSubscription)}
            </span>
          </div>
          <span className={`monthly-opr-small-value`}>{preSubscription}%</span>
        </div>

        <div className="monthly-operation-metric">
          <div className="monthly-operation-label">
            {translations[lang].Operation.deduction}
          </div>
          <span
            className={`monthly-opr-small-value ${coloring(deduction, preDeduction)}`}
          >
            {preDeduction
              ? (((deduction - preDeduction) / preDeduction) * 100).toFixed(2)
              : 0}
            %
          </span>
          <div className="monthly-operation-value-container">
            <span className="monthly-operation-value">{deduction}%</span>
            <span
              className={`monthly-operation-value ${coloring(deduction, preDeduction)}`}
            >
              {Arrowing(deduction, preDeduction)}
            </span>
          </div>
          <span className={`monthly-opr-small-value`}>{preDeduction}%</span>
        </div>
      </div>

      <div className="monthly-operation-top">
        <div className="monthly-operation-metric">
          <div className="monthly-operation-label">
            <Sigma className="monthly-operation-value arrowing-icon" />{" "}
            {translations[lang].Operation.totalOrder}
          </div>
          <span
            className={`monthly-opr-small-value ${coloring(preTotalOrders, totalOrders)}`}
          >
            {preTotalOrders
              ? (
                  ((totalOrders - preTotalOrders) / preTotalOrders) *
                  100
                ).toFixed(2)
              : 0}
            %
          </span>
          <div className="monthly-operation-value-container">
            <span className="monthly-operation-value">{totalOrders}</span>
            <span
              className={`monthly-operation-value ${coloring(preTotalOrders, totalOrders)}`}
            >
              {Arrowing(totalOrders, preTotalOrders)}
            </span>
          </div>
          <span className={`monthly-opr-small-value`}>{preTotalOrders}</span>
        </div>

        <div className="monthly-operation-metric">
          <div className="monthly-operation-label">
            <SaudiRiyal className="monthly-operation-value  arrowing-icon" />{" "}
            {translations[lang].Operation.totalAmount}:{" "}
          </div>
          <span
            className={`monthly-opr-small-value ${coloring(preTotalAmount, totalAmount)}`}
          >
            {preTotalAmount
              ? (
                  ((totalAmount - preTotalAmount) / preTotalAmount) *
                  100
                ).toFixed(2)
              : 0}
            %
          </span>
          <div className="monthly-operation-value-container">
            <span className="monthly-operation-value">{totalAmount}</span>
            <span
              className={`monthly-operation-value ${coloring(preTotalAmount, totalAmount)}`}
            >
              {Arrowing(totalAmount, preTotalAmount)}
            </span>
          </div>
          <span className={`monthly-opr-small-value`}>{preTotalAmount}</span>
        </div>
      </div>

      <div className="monthly-operation-bottom">
        <div className="monthly-operation-bottom-item">
          <div className="monthly-operation-label">
            <StarHalf className="monthly-operation-value  arrowing-icon" />
            {translations[lang].Operation.avg}:{" "}
            <span className="monthly-operation-value">{avgAmount}</span>
            <span className={`monthly-opr-small-value`}>{preAvgAmount}</span>
            <span
              className={`monthly-operation-value ${coloring(preAvgAmount, avgAmount)}`}
            >
              {Arrowing(avgAmount, preAvgAmount)}
            </span>
            <span
              className={`monthly-opr-small-value ${coloring(
                preAvgAmount,
                avgAmount,
              )}`}
            >
              {preAvgAmount
                ? (((avgAmount - preAvgAmount) / preAvgAmount) * 100).toFixed(2)
                : 0}
              %
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}